import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { TranslationService } from '../../../../../services/translation.service';
import TextFieldFormGroup from '../../../../Shared/TextFieldFormGroup';
import ValidationInfo from '../../../../../validation/validation-info';
import ValidationSummary from '../../../../Shared/ValidationSummary';
import { ValidatableComponent, ValidationHelper } from '../../../../../helpers/validation.helper';
import type IValidatable from '../../../../Shared/validatable.interface';
import HoneyPot, { type IHoneyPot } from '../../../../Shared/HoneyPot';
import Consent, { type IConsentState } from '../../../../Shared/Consent';
import { AnalyticsService } from '../../../../../services/analytics-service';
import type { OrganizationRegistrationApiModel } from './models/organization-registration.api-model';
import type { IServerValidationError } from '../../../../Shared/validatable.interface';
import { Status } from '../../../../../hooks/fetch.hook';
import NumberFieldFormGroup from '../../../../Shared/NumberFieldFormGroup';
import AntiforgeryService from '../../../../../services/anti-forgery.service';
import { SubmitState, SubmitSource } from '../models/submit-state.model';
import Alert from '../../../../Shared/Alert';

function OrganizationRegistrationForm({ onSubmitSuccessful }: { onSubmitSuccessful: (state: SubmitState) => void }) {
  // === STATE ===
  const [organizationNumberState, setOrganizationNumberState] = useState({ value: '', validationInfo: new ValidationInfo() });
  const [nameState, setNameState] = useState({ value: '', validationInfo: new ValidationInfo() });
  const [recipientState, setRecipientState] = useState({ value: '', validationInfo: new ValidationInfo() });
  const [emailState, setEmailState] = useState({ value: '', validationInfo: new ValidationInfo() });
  const [addressState, setAddressState] = useState({ value: '', validationInfo: new ValidationInfo() });
  const [postalCodeState, setPostalCodeState] = useState({ value: '', validationInfo: new ValidationInfo() });
  const [cityState, setCityState] = useState({ value: '', validationInfo: new ValidationInfo() });
  const [phoneNumberState, setPhoneNumberState] = useState({ value: '', validationInfo: new ValidationInfo() });
  const [numberOfCopiesState, setNumberOfCopiesState] = useState<{ value: number | undefined; validationInfo: ValidationInfo }>({ value: undefined, validationInfo: new ValidationInfo() });

  const [consentState, setConsentState] = useState<IConsentState>({ value: false, validationInfo: new ValidationInfo() });
  const [submitStatus, setSubmitStatus] = useState(Status.notperformed);
  const [showErrorAlert, setShowErrorAlert] = useState(false);

  // === REFS ===
  const organizationNumberRef = useRef<IValidatable>(null!);
  const nameRef = useRef<IValidatable>(null!);
  const recipientRef = useRef<IValidatable>(null!);
  const emailRef = useRef<IValidatable>(null!);
  const addressRef = useRef<IValidatable>(null!);
  const postalCodeRef = useRef<IValidatable>(null!);
  const cityRef = useRef<IValidatable>(null!);
  const phoneNumberRef = useRef<IValidatable>(null!);
  const numberOfCopiesRef = useRef<IValidatable>(null!);
  const consentRef = useRef<IValidatable>(null!);
  const honeyPotRef = useRef<IHoneyPot>(null!);

  function getValidatableComponents(): ValidatableComponent[] {
    return [
      { component: organizationNumberRef.current, validationInfo: organizationNumberState.validationInfo, setState: setOrganizationNumberState },
      { component: nameRef.current, validationInfo: nameState.validationInfo, setState: setNameState },
      { component: recipientRef.current, validationInfo: recipientState.validationInfo, setState: setRecipientState },
      { component: emailRef.current, validationInfo: emailState.validationInfo, setState: setEmailState },
      { component: addressRef.current, validationInfo: addressState.validationInfo, setState: setAddressState },
      { component: postalCodeRef.current, validationInfo: postalCodeState.validationInfo, setState: setPostalCodeState },
      { component: cityRef.current, validationInfo: cityState.validationInfo, setState: setCityState },
      { component: phoneNumberRef.current, validationInfo: phoneNumberState.validationInfo, setState: setPhoneNumberState },
      { component: numberOfCopiesRef.current, validationInfo: numberOfCopiesState.validationInfo, setState: setNumberOfCopiesState },
      { component: consentRef.current, validationInfo: consentState.validationInfo, setState: setConsentState },
    ];
  }

  function handleServerValidationErrors(errors: IServerValidationError[]): void {
    const invalidValidationInfos = ValidationHelper.handleServerValidationErrors(errors, getValidatableComponents());
    ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
      AnalyticsService.addEvent(AnalyticsService.StralkastarenRegistration, AnalyticsService.ErrorAction, e);
    });
  }

  // === VARIABLES ===
  const validationSummaryErrors: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponents());

  // === EVENT HANDLERS ===
  async function handleSubmitClick() {
    let isFormValid: boolean | undefined;
    let invalidValidationInfos: ValidationInfo[];
    await flushSync(async () => {
      if (submitStatus === Status.loading) return;

      setSubmitStatus(Status.notperformed);

      if (!honeyPotRef.current.isValid()) return;

      [isFormValid, invalidValidationInfos] = ValidationHelper.checkValidity(getValidatableComponents());
      if (!isFormValid) {
        ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
          AnalyticsService.addEvent(AnalyticsService.StralkastarenRegistration, AnalyticsService.ErrorAction, e);
        });
        return;
      }

      const application: OrganizationRegistrationApiModel = {
        organizationNumber: organizationNumberState.value.replace(/\D/g, ''),
        name: nameState.value,
        contactName: recipientState.value,
        email: emailState.value,
        address: addressState.value,
        postalCode: postalCodeState.value,
        city: cityState.value,
        phoneNumber: phoneNumberState.value,
        numberOfCopies: Number(numberOfCopiesState.value),
        isConsentGiven: consentState.value,
      };

      try {
        setSubmitStatus(Status.loading);
        const response = await fetch(`/api/stralkastaren/registration/organization`, {
          method: 'POST',
          body: JSON.stringify(application),
          headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
        });
        if (response.ok) {
          setSubmitStatus(Status.success);
          onSubmitSuccessful({ isSubmitted: true, status: Status.success, errorCode: undefined, source: SubmitSource.Organization });
          AnalyticsService.addEvent(AnalyticsService.StralkastarenRegistration, 'Skickat registrering - organisation', '');
        } else {
          setSubmitStatus(Status.failed);
          if (response.status === 400) {
            handleServerValidationErrors((await response.json()).errors);
            const validationSummary = document.getElementById('organization-registration-form-validation-summary');
            if (validationSummary) {
              validationSummary.scrollIntoView({ behavior: 'smooth', block: 'center' });
              validationSummary.focus();
            }
          } else {
            setShowErrorAlert(true);
            AnalyticsService.addEvent(AnalyticsService.StralkastarenRegistration, 'Skickat registrering - organisation - misslyckat', '');
          }
        }
      } catch {
        setSubmitStatus(Status.failed);
        setShowErrorAlert(true);
      }
    });
  }

  return (
    <div>
      <div id="organization-registration-form" className="organization-registration-form">
        <ValidationSummary id="organization-registration-form-validation-summary" validationSummaryErrors={validationSummaryErrors} />
        <TextFieldFormGroup
          ref={organizationNumberRef}
          required={true}
          labelText="Organisationsnummer"
          placeholder="XXXXXX-XXXX"
          inputId="organization-number"
          serverValidationName="organizationNumber"
          hideRequiredAsterisk={true}
          state={organizationNumberState}
          autocomplete="off"
          onChange={(value) => setOrganizationNumberState((ps) => ({ ...ps, value }))}
          onKeyUp={() => setOrganizationNumberState((ps) => ({ ...ps, validationInfo: new ValidationInfo() }))}
        />
        <TextFieldFormGroup
          ref={nameRef}
          required={true}
          labelText="Namn på företag/organisation"
          inputId="company-name"
          serverValidationName="name"
          autocomplete="off"
          hideRequiredAsterisk={true}
          state={nameState}
          onChange={(value) => setNameState((ps) => ({ ...ps, value }))}
          onKeyUp={() => setNameState((ps) => ({ ...ps, validationInfo: new ValidationInfo() }))}
        />
        <TextFieldFormGroup
          ref={recipientRef}
          required={true}
          labelText="Mottagarnamn"
          inputId="recipient-name"
          serverValidationName="recipient"
          autocomplete="off"
          hideRequiredAsterisk={true}
          state={recipientState}
          onChange={(value) => setRecipientState((ps) => ({ ...ps, value }))}
          onKeyUp={() => setRecipientState((ps) => ({ ...ps, validationInfo: new ValidationInfo() }))}
        />
        <TextFieldFormGroup
          ref={emailRef}
          required={true}
          labelText="E-post (kontaktperson)"
          inputId="email"
          serverValidationName="email"
          autocomplete="off"
          hideRequiredAsterisk={true}
          state={emailState}
          onChange={(value) => setEmailState((ps) => ({ ...ps, value }))}
          onKeyUp={() => setEmailState((ps) => ({ ...ps, validationInfo: new ValidationInfo() }))}
        />
        <TextFieldFormGroup
          ref={phoneNumberRef}
          required={true}
          labelText="Telefonnummer (kontaktperson)"
          inputId="phone-number"
          serverValidationName="phoneNumber"
          autocomplete="off"
          hideRequiredAsterisk={true}
          state={phoneNumberState}
          onChange={(value) => setPhoneNumberState((ps) => ({ ...ps, value }))}
          onKeyUp={() => setPhoneNumberState((ps) => ({ ...ps, validationInfo: new ValidationInfo() }))}
        />
        <TextFieldFormGroup
          ref={addressRef}
          required={true}
          labelText="Leveransadress"
          inputId="delivery-address"
          serverValidationName="address"
          autocomplete="off"
          hideRequiredAsterisk={true}
          state={addressState}
          onChange={(value) => setAddressState((ps) => ({ ...ps, value }))}
          onKeyUp={() => setAddressState((ps) => ({ ...ps, validationInfo: new ValidationInfo() }))}
        />
        <div className="row">
          <div className="col-12 col-md-4">
            <TextFieldFormGroup
              ref={postalCodeRef}
              required={true}
              labelText="Postnummer"
              inputId="postal-code"
              serverValidationName="postalCode"
              autocomplete="off"
              hideRequiredAsterisk={true}
              state={postalCodeState}
              onChange={(value) => setPostalCodeState((ps) => ({ ...ps, value }))}
              onKeyUp={() => setPostalCodeState((ps) => ({ ...ps, validationInfo: new ValidationInfo() }))}
            />
          </div>
          <div className="col-12 col-md-8">
            <TextFieldFormGroup
              ref={cityRef}
              required={true}
              labelText="Ort"
              inputId="city"
              serverValidationName="city"
              autocomplete="off"
              hideRequiredAsterisk={true}
              state={cityState}
              onChange={(value) => setCityState((ps) => ({ ...ps, value }))}
              onKeyUp={() => setCityState((ps) => ({ ...ps, validationInfo: new ValidationInfo() }))}
            />
          </div>
        </div>
        <NumberFieldFormGroup
          ref={numberOfCopiesRef}
          required={true}
          labelText="Antal exemplar (5-100)"
          inputId="number-of-copies"
          serverValidationName="numberOfCopies"
          hideRequiredAsterisk={true}
          state={numberOfCopiesState}
          onChange={(value) =>
            setNumberOfCopiesState((ps) => ({
              ...ps,
              value: value,
            }))
          }
          min={5}
          max={100}
          onKeyUp={() => setNumberOfCopiesState((ps) => ({ ...ps, validationInfo: new ValidationInfo() }))}
        />
        <Consent
          ref={consentRef}
          id="organization-registration-consent"
          consentText={TranslationService.get('stralkastarenregistrationpage.consenttext')}
          integrityPolicyInfo={TranslationService.get('stralkastarenregistrationpage.integritypolicyinfo')}
          termsAndConditions={TranslationService.get('stralkastarenregistrationpage.termsandconditionsheading')}
          className="mb-4"
          state={consentState}
          onChange={(value) => {
            setConsentState({ value, validationInfo: new ValidationInfo() });
          }}
        />
        <HoneyPot ref={honeyPotRef} />

        <div className={`d-grid ${showErrorAlert ? 'mb-3' : ''}`}>
          <button type="button" className={`btn btn-primary ${submitStatus === Status.loading ? 'btn-spinner' : ''}`} onClick={handleSubmitClick}>
            <span hidden={submitStatus === Status.loading}>Prenumerera</span>
          </button>
        </div>

        <Alert
          show={showErrorAlert}
          heading={TranslationService.get('stralkastarenregistrationpage.failedsubmissionheadingtext')}
          headingTag="h2"
          content={TranslationService.get('stralkastarenregistrationpage.failedsubmissionbodytext')}
          closable={false}
        />
      </div>
    </div>
  );
}

export default OrganizationRegistrationForm;